<template>
  <form @submit.prevent class="pa-8">
    <h1>{{ $t(Translations.howCanIHelp) }}</h1>
    <hr />

    <v-row class="mt-5 d-flex flex-sm-row flex-column">
      <v-col>
        <label for="firstName"
          >{{ $t(Translations.firstName) }} <span class="text-red">*</span></label
        >
        <v-text-field
          name="firstName"
          :disabled="isPreview"
          :error-messages="firstNameErrorMessage"
          v-model="messageDetails.firstName"
          color="blue600"
          variant="outlined"
          density="compact"
          class="w-full"
          @update:model-value="validateFirstName"
          @blur="firstNameOnceBlurred = true"
          maxlength="25"
        />
      </v-col>
      <v-col>
        <label for="lastName"
          >{{ $t(Translations.lastName) }} <span class="text-red">*</span></label
        >
        <v-text-field
          variant="outlined"
          density="compact"
          name="lastName"
          :disabled="isPreview"
          :error-messages="lastNameErrorMessage"
          v-model="messageDetails.lastName"
          color="blue600"
          class="w-full"
          @update:model-value="validateLastName"
          @blur="lastNameOnceBlurred = true"
          maxlength="25"
        />
      </v-col>
      <v-col>
        <label for="email"
          >{{ $t(Translations.emailAddress) }} <span class="text-red">*</span></label
        >
        <v-text-field
          name="email"
          :disabled="isPreview"
          :error-messages="emailErrorMessage"
          v-model="messageDetails.email"
          color="blue600"
          variant="outlined"
          density="compact"
          class="w-full"
          @update:model-value="validateEmail"
          @blur="emailOnceBlurred = true"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col
        ><label for="message"
          >{{ $t(Translations.message) }} <span class="text-red">*</span></label
        ></v-col
      >
      <v-col cols="12">
        <v-textarea
          name="message"
          variant="outlined"
          maxlength="300"
          :disabled="isPreview"
          v-model="messageDetails.message"
          @update:model-value="validateMessage"
          color="blue600"
          @blur="messageOnceBlurred = true"
          :placeholder="$t(Translations.messagePlaceholder)"
        />
        <p v-if="messageValidation" class="text-error">{{ $t(Translations.enterMessage) }}!</p>
      </v-col>
    </v-row>
    <OnexButton
      size="large"
      variant="outlined"
      :disabled="!validateForm"
      @click="sendMessage"
      class="mt-2"
    >
      {{ $t(Translations.send) }}
    </OnexButton>
  </form>
</template>

<script lang="ts">
import type { AgentInfo } from '@/interfaces/PegaAgent';
import { type CSSProperties, defineComponent, type PropType } from 'vue';
import { Translations } from '@/i18n/models';
import { emailCheck, nameCheck } from '@/constants/regexConstants';
import { OnexButton } from '@primerica/onex-ui-lib';
import { composeEmail } from '@/utils/common';

export default defineComponent({
  components: {
    OnexButton,
  },
  props: {
    agentData: {
      type: Object as PropType<AgentInfo>,
      required: false,
    },
    isPreview: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      Translations,
      messageDetails: {
        firstName: null as string | null,
        lastName: null as string | null,
        email: null as string | null,
        message: null as string | null,
      },
      firstNameOnceBlurred: false,
      lastNameOnceBlurred: false,
      emailOnceBlurred: false,
      messageOnceBlurred: false,
      isFirstNameValidated: false,
      isLastNameValidated: false,
      isEmailValidated: false,
      isMessageValidated: false,
    };
  },
  computed: {
    firstNameErrorMessage(): string {
      return (this.firstNameOnceBlurred || this.messageDetails.firstName?.length) &&
        !this.isFirstNameValidated
        ? this.$t(Translations.enterFirstName) + '!'
        : '';
    },
    lastNameErrorMessage(): string {
      return (this.lastNameOnceBlurred || this.messageDetails.lastName?.length) &&
        !this.isLastNameValidated
        ? this.$t(Translations.enterLastName) + '!'
        : '';
    },
    emailErrorMessage(): string {
      return (this.emailOnceBlurred || this.messageDetails.email?.length) && !this.isEmailValidated
        ? this.$t(Translations.enterEmail) + '!'
        : '';
    },
    messageValidation(): boolean {
      return !!(
        (this.messageOnceBlurred || this.messageDetails.message?.length) &&
        !this.isMessageValidated
      );
    },
    disabledButtonStyle(): CSSProperties {
      return !this.validateForm
        ? {
            border: '#ccc 1px solid',
            color: '#ccc',
          }
        : {};
    },
    validateForm(): boolean {
      return !!(
        this.isFirstNameValidated &&
        this.isLastNameValidated &&
        this.isEmailValidated &&
        this.isMessageValidated
      );
    },
  },
  methods: {
    validateFirstName() {
      this.isFirstNameValidated = !this.messageDetails.firstName?.length
        ? false
        : nameCheck.test(this.messageDetails.firstName);
    },

    validateLastName() {
      this.isLastNameValidated = !this.messageDetails.lastName?.length
        ? false
        : nameCheck.test(this.messageDetails.lastName);
    },

    validateEmail() {
      this.isEmailValidated = !this.messageDetails.email?.length
        ? false
        : emailCheck.test(this.messageDetails.email);
    },

    validateMessage() {
      this.isMessageValidated = !!this.messageDetails.message?.length;
    },

    resetInputFields() {
      this.messageDetails.firstName = '';
      this.messageDetails.lastName = '';
      this.messageDetails.email = '';
      this.messageDetails.message = '';
      this.isFirstNameValidated = false;
      this.isLastNameValidated = false;
      this.isEmailValidated = false;
      this.isMessageValidated = false;
      this.firstNameOnceBlurred = false;
      this.lastNameOnceBlurred = false;
      this.emailOnceBlurred = false;
      this.messageOnceBlurred = false;
    },

    sendMessage() {
      composeEmail(this.messageDetails.message ?? '');
      setTimeout(this.resetInputFields, 1000);
    },
  },
});
</script>

<style scoped>
.top-row-email-fields {
  column-gap: 1.25rem;
}

.top-row-email-fields .email-field-col {
  flex: 1 1 0;
  min-width: 0;
}

.top-row-email-fields .email-field-col :deep(.v-input) {
  width: 100%;
}
</style>
